import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_URL } from "../../../app/config";
import { axiosApiInstance } from "../../../app/axios";
import { ErrorToast, SuccessToast } from "../../../components/toast/Toasts";
import moment from "moment";
import {
  IPartAddtoOrders,
  IFindAllPartAddtoOrdersRequest,
  IActiveInActivePartAddtoOrdersRequest,
  IDeletePartAddtoOrdersRequest,
  IFindAllPartAddtoOrdersResponse,
} from "./../partAddtoOrdersModel";

export const createPartAddtoOrders = createAsyncThunk(
  "PartAddtoOrders/InsertPartAddtoOrders",
  async (partAddtoOrders: IPartAddtoOrders) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/PartAddtoOrders/InsertPartAddtoOrders`,
        partAddtoOrders,
      );
      SuccessToast(
        response.data?.Details || "Part Addto Orders created successfully",
      );
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error creating Part Addto Orders:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updatePartAddtoOrders = createAsyncThunk(
  "PartAddtoOrders/UpdatePartAddtoOrders",
  async (partAddtoOrders: IPartAddtoOrders) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/PartAddtoOrders/UpdatePartAddtoOrders`,
        partAddtoOrders,
      );
      SuccessToast(
        response.data?.Details || "Part Addto Orders updated successfully",
      );
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error updating Part Addto Orders:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deletePartAddtoOrders = createAsyncThunk(
  "PartAddtoOrders/DeletePartAddtoOrders",
  async (request: IDeletePartAddtoOrdersRequest) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/PartAddtoOrders/DeletePartAddtoOrders`,
        request,
      );
      SuccessToast(
        response.data?.Details || "Part Addto Orders deleted successfully",
      );
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error deleting Part Addto Orders:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getPartAddtoOrdersById = createAsyncThunk(
  "PartAddtoOrders/FindByIDPartAddtoOrders",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/PartAddtoOrders/FindByIDPartAddtoOrders`,
        { ID },
      );
      const result = response.data?.Data as IPartAddtoOrders;

      // Format dates if needed
      if (result) {
        result.Date = result.Date ? moment(result.Date).toDate() : new Date();

        // Ensure arrays are initialized
        result.RAWITEM = result.RAWITEM || [];
        result.ORDERITEM = result.ORDERITEM || [];
      }

      return result;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error fetching Part Addto Orders by ID:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const findAllPartAddtoOrders = createAsyncThunk(
  "PartAddtoOrders/FindAllPartAddtoOrders",
  async (request: IFindAllPartAddtoOrdersRequest = {}) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/PartAddtoOrders/FindAllPartAddtoOrders`,
        request,
      );
      const data = response.data?.Data || [];
      const totalCount = response.data?.TotalCount || data.length;

      // Format dates in the response if needed
      const formattedData = data.map((item: IPartAddtoOrders) => ({
        ...item,
        Date: item.Date ? moment(item.Date).toDate() : new Date(),
        RAWITEM: item.RAWITEM || [],
        ORDERITEM: item.ORDERITEM || [],
      }));

      return {
        Data: formattedData,
        TotalCount: totalCount,
        Page: request.page || 1,
        PageSize: request.pageSize || 10,
      } as IFindAllPartAddtoOrdersResponse;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error fetching Part Addto Orders:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const findAllActivePartAddtoOrders = createAsyncThunk(
  "PartAddtoOrders/FindAllActivePartAddtoOrders",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_URL}/PartAddtoOrders/FindAllActivePartAddtoOrders`,
      );
      const data = response.data?.Data || [];

      // Format dates in the response if needed
      const formattedData = data.map((item: IPartAddtoOrders) => ({
        ...item,
        Date: item.Date ? moment(item.Date).toDate() : new Date(),
        RAWITEM: item.RAWITEM || [],
        ORDERITEM: item.ORDERITEM || [],
      }));

      return formattedData;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error fetching active Part Addto Orders:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const activeInActivePartAddtoOrders = createAsyncThunk(
  "PartAddtoOrders/ActiveInActivePartAddtoOrders",
  async (request: IActiveInActivePartAddtoOrdersRequest) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_URL}/PartAddtoOrders/ActiveInActivePartAddtoOrders`,
        request,
      );
      SuccessToast(
        response.data?.Details ||
          `Part Addto Orders ${request.IsActive ? "activated" : "deactivated"} successfully`,
      );
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error updating Part Addto Orders status:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);
